import type { Student, StudentCaseRow } from "../types/database";
import { supabase } from "./supabase";
import { getImpersonatedUserId } from "./impersonate";
import {
  competitionStillInProgressForMyCase,
  listOpenCasesForStudent,
  MY_ACTIVE_REGISTRATION_STATUSES,
  STUDENT_CASE_SELECT,
  toStudentCaseRow,
  type StudentCaseRawRow,
} from "./studentCases";

async function companyNamesFor(ids: string[]): Promise<Map<string, string>> {
  const uniq = [...new Set(ids.filter(Boolean))];
  const out = new Map<string, string>();
  if (uniq.length === 0) return out;
  const { data: companies } = await supabase.from("companies").select("id, name").in("id", uniq);
  for (const c of companies ?? []) {
    if (typeof c.id === "string" && typeof c.name === "string" && c.name.trim()) out.set(c.id, c.name);
  }
  return out;
}

/** Competition IDs where the student has an enrolled registration (registered / submitted / active). */
export async function listRegisteredCompetitionIds(studentId: string): Promise<string[]> {
  const { data: regs } = await supabase
    .from("registrations")
    .select("competition_id")
    .eq("student_id", studentId)
    .in("status", [...MY_ACTIVE_REGISTRATION_STATUSES]);
  return [...new Set((regs ?? []).map((r) => r.competition_id).filter(Boolean))] as string[];
}

async function hydrateMyActiveCasesFromCompetitionIds(compIds: string[]): Promise<StudentCaseRow[]> {
  if (compIds.length === 0) return [];

  const { data: rows } = await supabase
    .from("competitions")
    .select(STUDENT_CASE_SELECT)
    .in("id", compIds)
    .in("status", ["open", "judging"]);

  if (!rows?.length) return [];
  const nameByCompany = await companyNamesFor((rows as unknown as StudentCaseRawRow[]).map((r) => r.company_id));
  const out: StudentCaseRow[] = [];
  for (const row of rows as unknown as StudentCaseRawRow[]) {
    const companyName = nameByCompany.get(row.company_id);
    if (!companyName) continue;
    const mapped = toStudentCaseRow(row, companyName);
    if (!competitionStillInProgressForMyCase(mapped)) continue;
    out.push(mapped);
  }
  out.sort((a, b) => {
    const ca = a.closes_at ? new Date(a.closes_at).getTime() : Number.POSITIVE_INFINITY;
    const cb = b.closes_at ? new Date(b.closes_at).getTime() : Number.POSITIVE_INFINITY;
    return ca - cb;
  });
  return out;
}

/** One registration query + parallel competition fetches for /student/cases. */
export async function loadStudentCasesPageData(
  student: Student | null,
  studentId: string
): Promise<{ myActive: StudentCaseRow[]; active: StudentCaseRow[]; soon: StudentCaseRow[] }> {
  const registeredIds = await listRegisteredCompetitionIds(studentId);
  const [myActive, browse] = await Promise.all([
    hydrateMyActiveCasesFromCompetitionIds(registeredIds),
    listOpenCasesForStudent(student, registeredIds),
  ]);
  return { myActive, active: browse.active, soon: browse.soon };
}

/** Count of programs the student is still in (matches MY ACTIVE CASES on /student/cases). */
export async function countRegisteredPrograms(studentId: string): Promise<number> {
  const rows = await listMyActiveCases(studentId);
  return rows.length;
}

/**
 * Competitions the student is enrolled in and that are still in progress
 * (registration status active/registered/submitted + competition open/judging, not past results_date).
 */
export async function listMyActiveCases(studentId: string): Promise<StudentCaseRow[]> {
  const compIds = await listRegisteredCompetitionIds(studentId);
  return hydrateMyActiveCasesFromCompetitionIds(compIds);
}

/** Distinct submissions the student uploaded or that belong to their teams. */
export async function countStudentSubmissions(studentId: string): Promise<number> {
  const { data: members } = await supabase.from("team_members").select("team_id").eq("student_id", studentId);
  const teamIds = [...new Set((members ?? []).map((m) => m.team_id).filter(Boolean))] as string[];

  const { data: bySelf } = await supabase.from("submissions").select("id").eq("submitted_by", studentId);
  const ids = new Set<string>((bySelf ?? []).map((r) => r.id));

  if (teamIds.length > 0) {
    const { data: byTeam } = await supabase.from("submissions").select("id").in("team_id", teamIds);
    for (const r of byTeam ?? []) ids.add(r.id);
  }

  return ids.size;
}

/** Whole days until the nearest future deliverable due date across registered programs, or null. */
export async function daysToNextDeliverableDeadline(studentId: string): Promise<number | null> {
  const { data: regs } = await supabase
    .from("registrations")
    .select("competition_id")
    .eq("student_id", studentId)
    .in("status", [...MY_ACTIVE_REGISTRATION_STATUSES]);
  const compIds = [...new Set((regs ?? []).map((r) => r.competition_id).filter(Boolean))] as string[];
  if (compIds.length === 0) return null;

  const nowIso = new Date().toISOString();
  const { data: dels } = await supabase
    .from("deliverables")
    .select("due_at")
    .in("competition_id", compIds)
    .not("due_at", "is", null)
    .gt("due_at", nowIso);

  const times = (dels ?? [])
    .map((d) => new Date(d.due_at as string).getTime())
    .filter((t) => !Number.isNaN(t));
  if (times.length === 0) return null;
  const minT = Math.min(...times);
  return Math.max(0, Math.ceil((minT - Date.now()) / 86_400_000));
}

export type DashboardDeadlineRow = {
  competitionId: string;
  competitionName: string;
  deliverableName: string;
  dueAt: string;
};

export async function listUpcomingDeadlines(studentId: string, limit = 8): Promise<DashboardDeadlineRow[]> {
  // Follow the view-as target when an admin is impersonating a student.
  const sid = getImpersonatedUserId() ?? studentId;
  const { data: regs } = await supabase
    .from("registrations")
    .select("competition_id")
    .eq("student_id", sid)
    .in("status", [...MY_ACTIVE_REGISTRATION_STATUSES]);
  const compIds = [...new Set((regs ?? []).map((r) => r.competition_id).filter(Boolean))] as string[];
  if (compIds.length === 0) return [];

  const nowIso = new Date().toISOString();
  // Pull more deliverables than the cap up-front, since we'll filter out any
  // the student's team has already submitted before slicing to `limit`.
  const fetchCap = Math.max(limit * 4, 24);
  const { data: dels } = await supabase
    .from("deliverables")
    .select("id, name, due_at, competition_id")
    .in("competition_id", compIds)
    .not("due_at", "is", null)
    .gt("due_at", nowIso)
    .order("due_at", { ascending: true })
    .limit(fetchCap);

  if (!dels?.length) return [];

  // Resolve the student's team in each registered competition, then collect
  // submitted deliverable ids so the dashboard "Upcoming deliverables" rail
  // doesn't keep showing rows the team has already turned in.
  const { data: teamMemberRows } = await supabase
    .from("team_members")
    .select("team_id, competition_id")
    .eq("student_id", studentId)
    .in("competition_id", compIds);
  const teamIds = [
    ...new Set((teamMemberRows ?? []).map((r) => r.team_id).filter(Boolean)),
  ] as string[];

  const submittedDeliverableIds = new Set<string>();
  if (teamIds.length > 0) {
    const { data: subs } = await supabase
      .from("submissions")
      .select("deliverable_id")
      .in("team_id", teamIds);
    for (const s of subs ?? []) {
      if (typeof s.deliverable_id === "string") submittedDeliverableIds.add(s.deliverable_id);
    }
  }

  const filtered = dels.filter(
    (d) => typeof d.id !== "string" || !submittedDeliverableIds.has(d.id)
  );
  const trimmed = filtered.slice(0, limit);

  if (trimmed.length === 0) return [];

  const { data: comps } = await supabase.from("competitions").select("id, name").in("id", compIds);
  const titleByComp = new Map<string, string>();
  for (const c of comps ?? []) {
    if (typeof c.id === "string" && typeof c.name === "string") titleByComp.set(c.id, c.name);
  }

  return trimmed.map((d) => ({
    competitionId: d.competition_id as string,
    competitionName: titleByComp.get(d.competition_id as string) ?? "Program",
    deliverableName: String(d.name ?? "Deliverable"),
    dueAt: d.due_at as string,
  }));
}

/** Same payload as MY ACTIVE CASES (dashboard left column). */
export async function listActiveRegisteredCompetitions(studentId: string): Promise<StudentCaseRow[]> {
  // Follow the view-as target when an admin is impersonating a student.
  return listMyActiveCases(getImpersonatedUserId() ?? studentId);
}

/** Open competitions that have not started yet (pre-release window). */
export async function listPreReleaseCompetitions(): Promise<StudentCaseRow[]> {
  const nowIso = new Date().toISOString();
  const { data: rows, error } = await supabase
    .from("competitions")
    .select(STUDENT_CASE_SELECT)
    .eq("status", "open")
    .not("opens_at", "is", null)
    .gt("opens_at", nowIso)
    .order("opens_at", { ascending: true });

  if (error || !rows?.length) return [];
  const nameByCompany = await companyNamesFor((rows as unknown as StudentCaseRawRow[]).map((r) => r.company_id));
  const out: StudentCaseRow[] = [];
  for (const row of rows as unknown as StudentCaseRawRow[]) {
    const companyName = nameByCompany.get(row.company_id);
    if (!companyName) continue;
    out.push(toStudentCaseRow(row, companyName));
  }
  return out;
}
